// Errors from any ZVIO API (Provyn, ZVIO HQ): no app state here, so every site can share it.

/** RFC 9457 problem details returned by ZVIO APIs. */
export interface Problem {
  type: string
  title: string
  status: number
  code: string
  detail?: string
  instance?: string
  errors?: { loc: (string | number)[]; msg: string; type: string }[]
  retry_after?: number
  /** Server errors: the request id to quote to support (matches the server log). */
  reference?: string
}

export class ApiError extends Error {
  readonly status: number
  readonly problem: Problem | null
  readonly body: unknown

  constructor(status: number, problem: Problem | null, body: unknown) {
    // For validation errors, the field-level reason is more useful than "Validation failed".
    const firstFieldError = problem?.errors?.[0]?.msg.replace(/^Value error, /, '')
    super(problem?.detail ?? firstFieldError ?? problem?.title ?? `Request failed (${status})`)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
    this.body = body
  }

  get code(): string | undefined {
    return this.problem?.code
  }

  /** Field-level validation messages keyed by field name, for forms. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    for (const e of this.problem?.errors ?? []) {
      const field = e.loc.at(-1)
      if (typeof field === 'string' && !(field in out)) out[field] = e.msg.replace(/^Value error, /, '')
    }
    return out
  }
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const ref = err.problem?.reference
    return ref ? `${err.message} (Error reference: ${ref.slice(0, 12)})` : err.message
  }
  if (err instanceof TypeError) return 'Network error. Check your connection and try again.'
  return 'Something went wrong. Please try again.'
}
